import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building, Phone, Mail, MapPin, Globe, User, Save, XCircle, Truck } from 'lucide-react';

const SupplierForm = ({ currentSupplier, onSave, onCancel }) => {
  const [supplier, setSupplier] = useState({
    id: null,
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    website: ''
  });

  useEffect(() => {
    if (currentSupplier) {
      setSupplier(currentSupplier);
    } else {
      setSupplier({
        id: null,
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        website: ''
      });
    }
  }, [currentSupplier]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSupplier(prevSupplier => ({
      ...prevSupplier,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(supplier);
  };

  return (
    <motion.div
      className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 md:p-12 w-full max-w-3xl mx-auto border border-gray-200"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 10 }}
    >
      <div className="flex flex-col items-center mb-8">
        <motion.div
          className="p-4 bg-gradient-to-br from-orange-500 to-red-600 rounded-full shadow-lg mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 150, damping: 10, delay: 0.1 }}
        >
          <Truck className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          {currentSupplier ? 'Editar Proveedor' : 'Añadir Nuevo Proveedor'}
        </h2>
        <p className="text-gray-500 text-center">
          {currentSupplier ? 'Modifica los datos de tu proveedor.' : 'Introduce los datos del nuevo proveedor.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div className="relative" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
          <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="name"
            placeholder="Nombre del Proveedor"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all duration-200 text-gray-800"
            value={supplier.name}
            onChange={handleChange}
            required
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="contact_person"
            placeholder="Persona de Contacto"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all duration-200 text-gray-800"
            value={supplier.contact_person}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="tel"
            name="phone"
            placeholder="Teléfono"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all duration-200 text-gray-800"
            value={supplier.phone}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="email"
            name="email"
            placeholder="Correo Electrónico"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all duration-200 text-gray-800"
            value={supplier.email}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative md:col-span-2" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.6 }}>
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="address"
            placeholder="Dirección"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all duration-200 text-gray-800"
            value={supplier.address}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative md:col-span-2" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
          <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="url"
            name="website"
            placeholder="Sitio Web (URL)"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all duration-200 text-gray-800"
            value={supplier.website}
            onChange={handleChange}
          />
        </motion.div>

        <div className="md:col-span-2 flex justify-end gap-4 mt-4">
          <motion.button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gray-200 text-gray-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <XCircle className="w-5 h-5" />
            Cancelar
          </motion.button>
          <motion.button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <Save className="w-5 h-5" />
            {currentSupplier ? 'Guardar Cambios' : 'Añadir Proveedor'}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default SupplierForm;